import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import {
  IBlogSubCategoryForm,
  IBlogSubCategoryItem,
} from "interfaces";
import { HttpError } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import { BLOG_SUB_CATEGORY_TYPE } from "utills/constants";
import { get } from "lodash";

export interface BlogSubCategoryFormProps {
  id?: string;
  defaultData?: IBlogSubCategoryItem;
  onSubmit: (data: IBlogSubCategoryForm) => void;
}

const BlogSubCategorySchema = z.object({
  is_active: z.boolean(),
  name_en: z.string().min(1, "This field is required"),
  name_vi: z.string().min(1, "This field is required"),
  category: z.string().min(1, "This field is required"),
});

export const BlogSubCategoryForm = ({ id, defaultData, onSubmit }: BlogSubCategoryFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IBlogSubCategoryForm, HttpError, IBlogSubCategoryForm>({
    defaultValues: {
      name_en: defaultData?.name_en ?? "",
      name_vi: defaultData?.name_vi ?? "",
      is_active: defaultData?.is_active ?? true,
      category: defaultData?.category ?? "",
    },
    resolver: zodResolver(BlogSubCategorySchema),
  });

  const _submit = (values: IBlogSubCategoryForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  return (
    <div className="w-full ">
      <form onSubmit={handleSubmit(_submit)}>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
              <div className="sm:col-span-3">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <Label required id="category" name="Category" />
                <Controller
                  control={control}
                  name={"category"}
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="category"
                        inputRef={ref}
                        options={BLOG_SUB_CATEGORY_TYPE}
                        id={`category`}
                        placeholder="Category"
                        defaultValue={
                          BLOG_SUB_CATEGORY_TYPE.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={BLOG_SUB_CATEGORY_TYPE.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, `category`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `category`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `category.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="sm:col-span-3">
                <Label required id="name_vi" name="Categories name vie" />
                <Input
                  id="name_vi"
                  type="text"
                  placeholder="Categories name vie"
                  error={errors["name_vi"]}
                  {...register("name_vi")}
                />
                {errors["name_vi"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_vi.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label required id="name_en" name="Categories name eng" />
                <Input
                  id="name_en"
                  type="text"
                  placeholder="Categories name eng"
                  error={errors["name_en"]}
                  {...register("name_en")}
                />
                {errors["name_en"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.name_en.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/mkt/sub-category${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};